/**
 * Order and deletion (docs/FORMAT.md §5). A device that writes to a bridge has a random name (`sender`) and a counter
 * (`seq`) that goes up by one for every message it writes there; a reader checks the run of each device for holes, repeats
 * and steps backwards. This file has the counters of this page and the check; the numbers travel in the envelope.
 */
import { toHex } from './base64url';
import { SENDER_BYTES } from './labels';
import { randomBytes } from './random';
import type { Position } from './envelope';

// ------------------------------------------------------- this page's counters

interface Run {
  readonly sender: Uint8Array<ArrayBuffer>;
  next: number;
}

const runs = new Map<string, Run>();

/** The place of the next message this page writes to `bridgeId`; each call takes a number. */
export function reservePosition(bridgeId: string): Position {
  let run = runs.get(bridgeId);
  if (run === undefined) {
    run = { sender: randomBytes(SENDER_BYTES), next: 0 };
    runs.set(bridgeId, run);
  }
  const seq = run.next;
  // u32 on the wire: a number that would wrap is refused, not reused
  if (seq >= 0xffffffff) throw new RangeError('This page has written as many messages to this bridge as the format counts.');
  run.next += 1;
  return { sender: run.sender, seq };
}

/** Gives a number back when its message was not accepted, if no later number was taken: a failed send leaves no hole. */
export function releasePosition(bridgeId: string, position: Position): void {
  const run = runs.get(bridgeId);
  if (run === undefined || toHex(run.sender) !== toHex(position.sender) || run.next !== position.seq + 1) return;
  run.next = position.seq;
}

// ------------------------------------------------------------------ the check

/** One message of the list, as the check sees it: its place, and the place of the message it removes if it is a deletion mark. */
export interface SequenceEntry {
  readonly id: string;
  readonly sender: string;
  readonly seq: number;
  readonly removes?: { readonly sender: string; readonly seq: number };
}

/** `altered` is not found by `checkSequence`: it is a message that did not open, which the caller adds. */
export type SequenceProblem = 'missing' | 'repeated' | 'reordered' | 'altered';

export interface SequenceReport {
  /** The ids that stay, in order: a repeated id is dropped. */
  readonly ids: readonly string[];
  readonly problems: readonly SequenceProblem[];
}

/**
 * Checks, device by device, that the numbers in list order are one run from 0 without a hole, a repeat or a step backwards.
 * A number that a deletion mark covers counts as present. The newest messages of a run are not checked: nothing after
 * them shows that they are gone (THREAT_MODEL.md).
 */
export function checkSequence(entries: readonly SequenceEntry[]): SequenceReport {
  const problems = new Set<SequenceProblem>();
  const seenIds = new Set<string>();
  const kept: SequenceEntry[] = [];
  for (const entry of entries) {
    if (seenIds.has(entry.id)) continue;
    seenIds.add(entry.id);
    kept.push(entry);
  }

  const bySender = new Map<string, { last: number; present: Set<number> }>();
  const covered = new Map<string, Set<number>>();
  for (const entry of kept) {
    const run = bySender.get(entry.sender) ?? { last: -1, present: new Set<number>() };
    bySender.set(entry.sender, run);
    if (run.present.has(entry.seq)) problems.add('repeated');
    else if (entry.seq < run.last) problems.add('reordered');
    run.present.add(entry.seq);
    run.last = Math.max(run.last, entry.seq);
    if (entry.removes !== undefined) {
      const set = covered.get(entry.removes.sender) ?? new Set<number>();
      set.add(entry.removes.seq);
      covered.set(entry.removes.sender, set);
    }
  }

  for (const sender of new Set([...bySender.keys(), ...covered.keys()])) {
    const have = new Set([...(bySender.get(sender)?.present ?? []), ...(covered.get(sender) ?? [])]);
    const top = Math.max(...have);
    for (let seq = 0; seq <= top; seq += 1) {
      if (!have.has(seq)) {
        problems.add('missing');
        break;
      }
    }
  }
  return { ids: kept.map((entry) => entry.id), problems: [...problems] };
}
